import React from 'react'
import { useRouter } from 'next/router'
import { useFormContext } from 'react-hook-form'

import useSearchFilters from 'app/SearchPage/hooks/useSearchFilters'
import useSearchQuery from 'app/SearchPage/hooks/useSearchQuery'
import useSETrackEvent from 'app/SearchPage/hooks/useSETrackEvent'
import {
  useContentSearchFilter,
  useContentSearchFilterOnboarding,
  useContentSearchTracker,
} from 'stores/content-search'

export const useSearchFilterState = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  const router = useRouter()
  const { clearErrors, setValue } = useFormContext()
  const [selectedFormats, setSelectedFormats] = React.useState<string[]>([])
  const [selectedTopics, setSelectedTopics] = React.useState<string[]>([])
  const hasAnyFilterSelected = React.useMemo(
    () => selectedFormats.length || selectedTopics.length,
    [selectedFormats.length, selectedTopics.length]
  )
  const { searchFiltersData } = useSearchFilters()
  const { searchQueryFromURL } = useSearchQuery()
  const contentSearchFilterOnboardingStore = useContentSearchFilterOnboarding()
  const contentSearchFilterStore = useContentSearchFilter()
  const contentSearchTrackerStore = useContentSearchTracker()
  const { SETrackSearchResultsPage } = useSETrackEvent()

  const submitFilterHandler = React.useCallback(() => {
    setValue('searchQuery', searchQueryFromURL)
    clearErrors('searchQuery')
    SETrackSearchResultsPage('CLICK_SEARCH_FILTER_RESULTS', {
      event_trigger: contentSearchTrackerStore.eventTrigger,
    })
    onClose()
    setTimeout(() => {
      if (!contentSearchFilterOnboardingStore.isFinished) {
        contentSearchFilterOnboardingStore.setFinished()
      }
      window.scrollTo({ top: 0 })
    }, 500)

    router.replace({
      pathname: '/pencarian',
      query: {
        q: searchQueryFromURL,
        ...(selectedFormats.length ? { formats: selectedFormats } : {}),
        ...(selectedTopics.length ? { topics: selectedTopics } : {}),
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFormats, selectedTopics])

  const resetFilterHandler = React.useCallback(() => {
    setSelectedFormats([])
    setSelectedTopics([])
    SETrackSearchResultsPage('CLICK_SEARCH_FILTER_RESET', {
      event_trigger: contentSearchTrackerStore.eventTrigger,
    })
  }, [SETrackSearchResultsPage, contentSearchTrackerStore.eventTrigger])

  const clickOptionHandler = React.useCallback(
    (isChecked: boolean, key: string) => {
      if (isChecked) {
        SETrackSearchResultsPage('CLICK_SEARCH_FILTER_OPTIONS', {
          filter: key,
          event_trigger: contentSearchTrackerStore.eventTrigger,
        })
      }
    },
    [SETrackSearchResultsPage, contentSearchTrackerStore.eventTrigger]
  )

  React.useEffect(() => {
    if (isOpen) {
      setSelectedFormats(contentSearchFilterStore.selectedFormats)
      setSelectedTopics(contentSearchFilterStore.selectedTopics)
    }
  }, [
    contentSearchFilterStore.selectedFormats,
    contentSearchFilterStore.selectedTopics,
    isOpen,
  ])

  return {
    selectedFormats,
    setSelectedFormats,
    selectedTopics,
    setSelectedTopics,
    hasAnyFilterSelected,
    submitFilterHandler,
    resetFilterHandler,
    clickOptionHandler,
    searchFiltersData,
  }
}
